import { useEffect, useRef, useState } from 'react';

import { Link } from '@remix-run/react';
import { Button } from '~/components/ui/button';
import { formatDateTime, getFormattedDistanceToNow } from '~/lib/utils';
import { getAuctionsList } from '~/services/api/auctions';
import { AuctionType } from '~/types';
import { format, isAfter, isBefore } from 'date-fns';
import { Calendar, Eye, Video } from 'lucide-react';

import { AuctionImages } from './auction-images';

export type Auction = NonNullable<Awaited<ReturnType<typeof getAuctionsList>>>['list'][number];

export type AuctionCardProps = {
  bgColor?: string;
  auction: Auction;
};

export const AuctionCard = ({ bgColor = 'bg-white', auction }: AuctionCardProps) => {
  const [isAuctionStarted, setIsAuctionStarted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (auction) {
      const updateRemainingTime = () => {
        const now = new Date();
        setIsAuctionStarted(now >= new Date(auction.auctionStart) && now <= new Date(auction.auctionEnd));
        const auctionEnd = new Date(auction?.auctionEnd ?? 0);
        const timeLeft = getFormattedDistanceToNow(auctionEnd);
        setTimeRemaining(timeLeft);

        if (new Date() >= auctionEnd) {
          clearInterval(intervalRef.current as NodeJS.Timeout);
        }
      };

      intervalRef.current = setInterval(updateRemainingTime, 1000);

      return () => clearInterval(intervalRef.current as NodeJS.Timeout);
    }
  }, [auction]);

  const auctionEnded = auction ? isAfter(new Date(), new Date(auction.auctionEnd)) : false;

  const isLive = auction.products.some((product) => product.liveBiddingMode);

  const displayImages = (auction as any).displayImages as string[] | undefined;
  const auctionImages = displayImages && displayImages.length > 0
    ? displayImages.map((src) => ({ file: { src, name: '' } }))
    : auction.products.filter((p) => p.images.length > 0).map((p) => p.images[0]);

  return (
    <div className={`flex w-full basis-1/4 flex-col ${bgColor} relative gap-3 rounded border`}>
      {isLive && (
        <div className="absolute flex flex-row items-center gap-1 bg-red-500 px-1 text-white">
          <p className="text-xs font-normal">Live now </p>
          <Eye className="h-3 w-3" />
          {/* <p className="text-xs font-normal">{auction.liveNumber}</p> */}
        </div>
      )}

      <div className="flex w-full flex-row space-x-[1px]">
        <AuctionImages images={auctionImages} />
      </div>
      <div className="mb-4 flex flex-col gap-2 px-[5%]">
        <p className="text-xs font-normal text-[#646464]">{auction.auctionHouse?.name}</p>
        <p className="font-semibold text-black">{auction.name}</p>
        <div className="flex flex-row gap-2">
          <Calendar className="h-3 w-3" color="#646464" />
          <p className="text-xs font-normal text-[#646464]">
            Start: {format(auction.auctionStart, 'ccc, do MMM yyyy h:mm aaa zzzz')}
          </p>
        </div>
        {auction.auctionType === AuctionType.TIMEONLINE ? (
          <div className="flex flex-row gap-2">
            <Calendar className="h-3 w-3" color="#646464" />
            <p className="text-xs font-normal text-[#646464]">End: {formatDateTime(auction.auctionEnd)} GMT+8</p>
          </div>
        ) : auction.auctionType === 'Time2Live' && isLive ? (
          <div className="flex flex-row items-center gap-2">
            <Video className="h-3 w-3" color="#646464" />
            <p className="text-xs text-red-500">Auction is Live. Bid now!</p>
          </div>
        ) : (
          <div className="flex flex-row items-center gap-2">
            <Video className="h-3 w-3" color="#646464" />
            {auctionEnded ? (
              <p className="text-xs text-slate-500">Auction ended</p>
            ) : (auction as any).liveAuctionDate ? (
              <p className="text-xs font-normal text-[#646464]">
                Live auction: {formatDateTime((auction as any).liveAuctionDate)} GMT+8
              </p>
            ) : null}
          </div>
        )}
        {auctionEnded ? (
          <Button disabled={true}>Auction Ended</Button>
        ) : (
          <Button variant={'primary'} disabled={!isAuctionStarted}>
            Bid Now
          </Button>
        )}
        <Link to={`/auction-detail/${auction.slug}`}>
          <Button variant={'outline'} className="w-full">
            View Collection
          </Button>
        </Link>
      </div>
    </div>
  );
};
